import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, RotateCcw, Sparkles, Check, Copy, ChevronDown, Phone } from "lucide-react";
import { QUIZZES, evaluate, regionContent } from "@/lib/clinicData";
import { API } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

function Strip({ title, open, onToggle, children, tone }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3" data-testid={`strip-${title.split(" ")[0].toLowerCase()}`}>
        <span className="text-[11px] tracking-[0.18em] uppercase" style={{ color: tone }}>{title}</span>
        <ChevronDown size={14} className="text-white/40 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function UrgencyQuiz({ region, onClose, onLog }) {
  const quiz = QUIZZES[region.quiz] || QUIZZES.torso;
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [ai, setAi] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [openStrip, setOpenStrip] = useState("act");

  const reset = () => { setAnswers({}); setStep(0); setResult(null); setAi(null); setOpenStrip("act"); };

  const pick = (q, idx) => {
    const next = { ...answers, [q.id]: idx };
    setAnswers(next);
    if (step + 1 >= quiz.length) setResult(evaluate(region.quiz, next));
    else setStep(step + 1);
  };

  if (result) {
    const b = result.band;
    const content = regionContent(region, result.flags);
    const askNovcovek = async () => {
      setLoadingAi(true);
      try {
        const { data } = await axios.post(`${API}/clinic/explain`, {
          region: region.label, urgency: b.title, answers: result.chosen, causes: content.causes,
        });
        setAi(data.text);
        setOpenStrip("ai");
      } catch (e) { toast("Novcovek could not be reached. The read above still stands."); }
      finally { setLoadingAi(false); }
    };

    return (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="quiz-result">
        <div className="rounded-[1.5rem] p-5 relative overflow-hidden grain" style={{ background: b.tone }}>
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/70">{region.label}</p>
          <p className="font-display text-4xl text-white mt-2 leading-none" data-testid="quiz-window">{b.window}</p>
          <h3 className="font-display text-lg text-white/90 mt-2">{b.title}</h3>
          <p className="text-sm text-white/80 mt-2">{b.line}</p>
          {b.key === "emergency" && (
            <a href="tel:112" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold" style={{ color: b.tone }} data-testid="quiz-call-112">
              <Phone size={13} /> Call 112
            </a>
          )}
        </div>

        <Strip title="Do this first" tone="#F5A088" open={openStrip === "act"} onToggle={() => setOpenStrip(openStrip === "act" ? null : "act")}>
          <ol className="space-y-2">
            {b.steps.map((s, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-white/80">
                <span className="shrink-0 w-5 h-5 rounded-full bg-white/10 text-[10px] flex items-center justify-center text-white/60">{i + 1}</span>{s}
              </li>
            ))}
          </ol>
        </Strip>

        <Strip title="What it could be" tone="#F5A088" open={openStrip === "causes"} onToggle={() => setOpenStrip(openStrip === "causes" ? null : "causes")}>
          <div className="space-y-1.5">
            {content.causes.map((c, i) => (
              <div key={i} className="rounded-xl px-3 py-2 text-sm text-white/75" style={{ background: "rgba(255,255,255,0.05)" }}>{c}</div>
            ))}
          </div>
          <p className="text-[11px] text-white/30 mt-2">Possibilities, not a diagnosis. Nobody here has examined you.</p>
        </Strip>

        <Strip title="Take these to the doctor" tone="#F5A088" open={openStrip === "ask"} onToggle={() => setOpenStrip(openStrip === "ask" ? null : "ask")}>
          <ul className="space-y-1.5">
            {content.ask.map((a, i) => <li key={i} className="text-sm text-white/80 flex gap-2"><span style={{ color: b.tone }}>—</span>{a}</li>)}
          </ul>
          <button
            onClick={() => { navigator.clipboard.writeText(content.ask.map((a) => `- ${a}`).join("\n")); toast("Questions copied."); }}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-white/55 hover:text-white"
            data-testid="quiz-copy-questions"
          ><Copy size={11} /> copy them</button>
        </Strip>

        {ai && (
          <Strip title="Novcovek on this read" tone="#C7B6F5" open={openStrip === "ai"} onToggle={() => setOpenStrip(openStrip === "ai" ? null : "ai")}>
            <p className="text-sm text-white/80 leading-relaxed" data-testid="quiz-ai-text">{ai}</p>
          </Strip>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button onClick={() => onLog?.({ region: region.label, band: b.key, window: b.window })} className="flex-1 min-w-[150px] rounded-full bg-white px-4 py-2.5 text-sm font-semibold" style={{ color: "#7A1E12" }} data-testid="quiz-save-log">
            <Check size={13} className="inline mr-1.5" />Keep in my log
          </button>
          {!ai && (
            <button onClick={askNovcovek} disabled={loadingAi} className="rounded-full px-4 py-2.5 text-sm text-white/85 disabled:opacity-50" style={{ background: "rgba(255,255,255,0.1)" }} data-testid="quiz-ask-ai">
              <Sparkles size={13} className="inline mr-1.5" />{loadingAi ? "reading" : "Ask Novcovek"}
            </button>
          )}
          <button onClick={reset} className="rounded-full border border-white/20 px-4 py-2.5 text-white/60 hover:bg-white/10" data-testid="quiz-restart" aria-label="Start the read again"><RotateCcw size={13} /></button>
        </div>
      </motion.div>
    );
  }

  const q = quiz[step];
  return (
    <div data-testid="quiz-panel">
      <div className="flex items-center gap-1.5 mb-5">
        {quiz.map((_, i) => (
          <span key={i} className="h-[3px] flex-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.12)" }}>
            <motion.span className="block h-full" style={{ background: "#E2604A" }} initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.35 }} />
          </span>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={q.id} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/35">{region.label} · {step + 1} of {quiz.length}</p>
          <h3 className="font-display text-xl sm:text-2xl text-white mt-2 leading-snug">{q.text}</h3>
          <div className="mt-4 space-y-2">
            {q.options.map((o, i) => (
              <button
                key={i}
                onClick={() => pick(q, i)}
                className="w-full text-left rounded-2xl px-4 py-3 text-sm text-white/85 transition-colors"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(226,96,74,0.22)"; e.currentTarget.style.borderColor = "rgba(245,160,136,0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
                data-testid={`quiz-option-${step}-${i}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-5 flex items-center justify-between">
        <button onClick={() => (step > 0 ? setStep(step - 1) : onClose())} className="text-xs text-white/45 hover:text-white inline-flex items-center gap-1.5" data-testid="quiz-back">
          <ArrowLeft size={12} /> {step > 0 ? "previous" : "close"}
        </button>
        <p className="text-[10px] text-white/25">rule based · never a diagnosis</p>
      </div>
    </div>
  );
}
